import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StopPlaceOrganisationFieldsFragment } from '../../../../../generated/graphql';
import { useWrapInContextNavigation } from '../../../../common/Forms/NavigationBlocker';
import { Modal, ModalBody, ModalHeader } from '../../../../common/Modals';
import {
  OrganisationDetailsForm,
  OrganisationDetailsFormState,
} from './OrganisationDetailsForm';
import { useUpsertOrganisation } from './useUpsertOrganisation';

const testIds = {
  modal: 'OrganisationDetailsModal',
  title: 'OrganisationDetailsModal::title',
};

type OrganisationDetailsModalProps = {
  readonly isOpen: boolean;
  readonly organisation?: StopPlaceOrganisationFieldsFragment | null;
  readonly onClose: () => void;
  readonly onSubmit: (
    org: Pick<StopPlaceOrganisationFieldsFragment, 'id'>,
  ) => void;
};

function mapOrganisationToFormState(
  organisation: StopPlaceOrganisationFieldsFragment | undefined | null,
): Partial<OrganisationDetailsFormState> {
  return {
    id: organisation?.id ?? undefined,
    name: organisation?.name ?? undefined,
    privateContactDetails: {
      email: organisation?.privateContactDetails?.email,
      phone: organisation?.privateContactDetails?.phone,
    },
  };
}

export const OrganisationDetailsModal: FC<OrganisationDetailsModalProps> = ({
  isOpen,
  organisation,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const wrapInContextNavigation = useWrapInContextNavigation(
    'OrganisationDetailsForm',
  );

  const { upsertOrganisation, defaultErrorHandler } = useUpsertOrganisation();

  const onConfirm = async (state: OrganisationDetailsFormState) => {
    try {
      const result = await upsertOrganisation(state);
      if (result?.id) {
        onSubmit(result);
      }
    } catch (err) {
      defaultErrorHandler(err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={wrapInContextNavigation(onClose)}
      testId={testIds.modal}
    >
      <ModalHeader
        onClose={onClose}
        heading={
          organisation?.id
            ? t(($) => $.stopDetails.maintenance.organisation.modalTitleEdit)
            : t(($) => $.stopDetails.maintenance.organisation.modalTitleCreate)
        }
        testId={testIds.title}
      />
      <ModalBody className="w-[500px]">
        <OrganisationDetailsForm
          defaultValues={mapOrganisationToFormState(organisation)}
          onSubmit={onConfirm}
          onCancel={onClose}
        />
      </ModalBody>
    </Modal>
  );
};
